// =============================================================================
// Finance invoice review detail — one invoice through finance's eyes:
// the review row's 3-way match verdict + the live Epicor aggregate
// (GET /api/invoices/{n}?include=order), with the approve / reject / hold /
// send action bar.
//
// Approve honors the server's acknowledgment contract: a flagged match returns
// 400 { requiresAcknowledgment, matchStatus } → confirm → re-POST with
// { acknowledge: true }. CapBreach is a HARD block — the server refuses it
// even with acknowledge, and we surface its message verbatim.
//
// Per-installment work (upload PO / validate / send / override) lives on the
// AR detail page (/invoices/{n}) which renders the server-driven packet —
// one detail component owns that flow, so this page links to it.
// =============================================================================
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { FaChevronLeft, FaSync } from "react-icons/fa";
import { apiFetch, hasAction } from "../utils/auth";
import AttachmentsButton from "../components/ReferenceDocsPanel";
import { MATCH_BADGE, capFromTerms, decisionLabel, fmtDate, money } from "./financeShared";
import "../pagecss/Webappcss.css";
import "../pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

function Card({ title, right, children }) {
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>{right}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: "#94A3B8", marginRight: 6 }}>{label}:</span>
      <span>{children ?? "—"}</span>
    </div>
  );
}

export default function FinanceInvoiceDetail() {
  const { invoiceNum } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [review, setReview] = useState(location.state?.review || null);
  const [agg, setAgg] = useState(null);          // live Epicor aggregate
  const [installments, setInstallments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Review row (always re-fetched so decisions/holds show current state) + the live aggregate.
      let rev = null;
      const qr = await apiFetch(`${API_URL}/api/invoices/review-queue?search=${encodeURIComponent(invoiceNum)}&pageSize=10`);
      const q = await qr.json().catch(() => null);
      rev = (q?.items || []).find((x) => String(x.invoiceNum) === String(invoiceNum)) || review || null;
      setReview(rev);
      const ar = await apiFetch(`${API_URL}/api/invoices/${encodeURIComponent(invoiceNum)}?include=order`);
      const a = await ar.json().catch(() => null);
      if (!ar.ok || a?.success === false) throw new Error(a?.error || a?.message || `Invoice ${invoiceNum} not found (HTTP ${ar.status}).`);
      setAgg(a?.invoice || a);
      if (rev && rev.planCategory !== "CASH") {
        const ir = await apiFetch(`${API_URL}/api/invoices/review/${rev.id}/installments`);
        const inst = await ir.json().catch(() => []);
        setInstallments(Array.isArray(inst) ? inst : []);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [invoiceNum, review]);

  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const header = agg?.header || {};
  const lines = agg?.lines || [];
  const sched = agg?.paymentSchedule || [];
  const order = agg?.linkedOrder || null;
  const isCash = review?.planCategory === "CASH";

  // ── review actions ─────────────────────────────────────────────────────────
  const post = async (path, body) => {
    const r = await apiFetch(`${API_URL}${path}`, { method: "POST", ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    const d = await r.json().catch(() => null);
    return { ok: r.ok, status: r.status, d };
  };

  const run = async (key, fn) => {
    setBusy(key); setError(null); setNote(null);
    try { await fn(); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const approve = () => run("approve", async () => {
    let { ok, status, d } = await post(`/api/invoices/review/${review.id}/approve`, {});
    if (!ok && status === 400 && d?.requiresAcknowledgment) {
      if (!window.confirm(`Match is '${d.matchStatus}'. Approve anyway with acknowledge=true?`)) return;
      ({ ok, d } = await post(`/api/invoices/review/${review.id}/approve`, { acknowledge: true, note: null }));
    }
    if (!ok) throw new Error(d?.error || d?.message || "Approve failed.");
    setNote(d?.message || "Approved.");
  });

  const reject = () => run("reject", async () => {
    const reason = window.prompt("Return this invoice for correction — reason:");
    if (!reason) return;
    const { ok, d } = await post(`/api/invoices/review/${review.id}/reject`, { reason });
    if (!ok) throw new Error(d?.error || "Reject failed.");
    setNote(d?.message || "Rejected — returned for correction.");
  });

  const holdToggle = () => run("hold", async () => {
    if (review.isHeld) {
      const { ok, d } = await post(`/api/invoices/review/${review.id}/unhold`);
      if (!ok) throw new Error(d?.error || "Unhold failed.");
      setNote("Hold cleared.");
    } else {
      const reason = window.prompt("Hold this invoice (customer dispute) — reason:");
      if (!reason) return;
      const { ok, d } = await post(`/api/invoices/review/${review.id}/hold`, { reason });
      if (!ok) throw new Error(d?.error || "Hold failed.");
      setNote("Held — blocked from posting until resolved.");
    }
  });

  const send = () => run("send", async () => {
    if (!window.confirm(`Send invoice ${invoiceNum} to the customer AP contact (CC the rep) now?\n(SIMULATED in dev until InvoiceSending:Enabled.)`)) return;
    const { ok, d } = await post(`/api/invoices/review/${review.id}/send`);
    if (!ok) throw new Error(d?.error || d?.message || "Send failed.");
    setNote(d?.message || "Sent.");
  });

  const revalidate = () => run("revalidate", async () => {
    const { ok, d } = await post(`/api/invoices/review/${review.id}/revalidate`);
    if (!ok) throw new Error(d?.error || "Revalidate failed.");
    setNote(!d?.foundInEpicor ? "⚠ Not in Epicor (voided?)"
      : d?.amountChanged ? `Amount changed → ${d.matchStatus} · ${d.driftAction || ""}`
      : `Re-matched → ${d?.matchStatus}`);
  });

  const verifyInstallments = () => run("verify-inst", async () => {
    const { ok, d } = await post(`/api/invoices/review/${review.id}/verify-installments`);
    if (!ok) throw new Error(d?.error || "Verify failed.");
    setNote(`Checked ${d?.installmentsChecked ?? "?"} · matched ${d?.matched ?? 0} · short ${d?.short ?? 0} · over ${d?.over ?? 0}${d?.capBreach ? ` · ⚠ capBreach ${d.capBreach}` : ""} → ${d?.reviewVerdict ?? ""}`);
  });

  const dec = review ? decisionLabel(review) : null;

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div>
          <div className="qw-header-title">Invoice #{invoiceNum}</div>
          <div className="qw-header-sub">{review?.customerName || header.customerName || "…"}{review ? ` · ${review.planCategory}` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none" }} onClick={load}><FaSync /></Button>
          <AttachmentsButton entityType="Invoice" entityId={invoiceNum} />
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={() => navigate(-1)}>
            <FaChevronLeft style={{ marginRight: 6 }} /> Back
          </Button>
        </div>
      </div>

      {note && <Alert variant="success" dismissible onClose={() => setNote(null)} style={{ fontSize: 13 }}>{note}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ fontSize: 13 }}>{error}</Alert>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
          <Spinner animation="border" size="sm" /> Loading invoice… (live Epicor read, ~2s)
        </div>
      ) : (
        <>
          {/* Workflow & 3-way match */}
          {review && (
            <Card title="⚖ Workflow & 3-way match" right={
              !isCash ? null : hasAction("00075", true) && (
                <Button size="sm" variant="outline-secondary" disabled={!!busy} onClick={revalidate}>
                  {busy === "revalidate" ? <Spinner animation="border" size="sm" /> : "↻ Reload & re-validate"}
                </Button>
              )
            }>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                <Badge bg={MATCH_BADGE[review.matchStatus] || "secondary"} style={{ fontSize: 13 }}>{review.matchStatus}</Badge>
                {dec && <Badge bg={dec.bg} text={dec.bg === "warning" ? "dark" : undefined}>{dec.text}</Badge>}
                {review.isHeld && <Badge bg="warning" text="dark" title={review.holdReason || undefined}>🔒 held</Badge>}
              </div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 8 }}>
                {[["ORDER", review.orderAmount], ["PO SUBMITTED", review.expectedPOAmount], ["INVOICE", review.invoiceAmount]].map(([l, v]) => (
                  <div key={l} style={{ textAlign: "center", minWidth: 110, padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 10 }}>
                    <div style={{ fontSize: 10.5, color: "#94A3B8", letterSpacing: ".05em" }}>{l}</div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{money(v)}</div>
                  </div>
                ))}
              </div>
              {review.matchDetail && (
                <div style={{ fontSize: 12.5, color: ["Matched", "NoCharge"].includes(review.matchStatus) ? "#16A34A" : "#B45309" }}>
                  {review.matchDetail}
                </div>
              )}
              {review.isHeld && review.holdReason && <div style={{ fontSize: 12.5, color: "#B45309" }}>Hold reason: {review.holdReason}</div>}
            </Card>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Card title="👤 Parties">
              <Field label="Sold-To">{header.customerName} {header.customerCustID ? `(${header.customerCustID})` : ""}</Field>
              <Field label="Bill-To (AP)">{header.customerBTName || header.btCustID}</Field>
              <Field label="AP contact">{header.contactName}</Field>
              <Field label="AP email">{header.contactEmailAddr || <i style={{ color: "#94A3B8" }}>no email on invoice</i>}</Field>
              <Field label="AP phone">{header.contactPhoneNum}</Field>
              <Field label="Sales reps">
                {[1, 2, 3, 4, 5].map((i) => header[`salesRepName${i}`] || header[`salesRepCode${i}`]).filter(Boolean).join(" · ") || "—"}
              </Field>
            </Card>
            <Card title="📄 Invoice">
              <Field label="Type">{header.invoiceType} {header.posted != null && <Badge bg={header.posted ? "dark" : "secondary"} style={{ marginLeft: 6 }}>{header.posted ? "Posted" : "Unposted"}</Badge>}</Field>
              <Field label="Terms">{header.termsCode}{header.termsCodeDescription ? ` — ${header.termsCodeDescription}` : ""}</Field>
              <Field label="Invoice date">{fmtDate(header.invoiceDate)}</Field>
              <Field label="Due date">{fmtDate(header.dueDate)}</Field>
              <Field label="Customer PO">{header.poNum || review?.customerPONum}</Field>
              <Field label="Sales order">{header.orderNum || review?.orderNum}</Field>
              <Field label="Pack slip">{header.packNum || review?.packSlipNum}</Field>
              <div style={{ borderTop: "1px solid #F1F5F9", marginTop: 8, paddingTop: 8 }}>
                <Field label="Subtotal">{money(header.docSubTotal ?? header.subTotal)}</Field>
                <Field label="Tax">{money(header.docTaxAmt ?? header.taxAmt)}</Field>
                <Field label="Invoice amount"><b>{money(header.docInvoiceAmt ?? header.invoiceAmt ?? review?.invoiceAmount)}</b></Field>
                <Field label="Balance">{money(header.docInvoiceBal ?? header.invoiceBal)}</Field>
              </div>
            </Card>
          </div>

          {/* Payment schedule */}
          {sched.length > 0 && (
            <Card title="📆 Payment schedule">
              <Table size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
                <thead><tr><th>#</th><th>Days</th><th>Due</th><th>%</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
                <tbody>
                  {sched.map((s) => (
                    <tr key={s.paySeq}>
                      <td>{s.paySeq}</td><td>{s.payDays}</td><td>{fmtDate(s.payDueDate ?? s.dueDate)}</td>
                      <td>{s.payPercent != null ? Number(s.payPercent).toFixed(4) : "—"}</td>
                      <td style={{ textAlign: "right" }}>{money(s.docPayAmount ?? s.payAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Sub-invoices (BK / PoT / CTO) */}
          {!isCash && (installments || []).length > 0 && (
            <Card title={`🧾 Sub-invoices (${installments.length})`} right={
              <Button size="sm" variant="outline-primary" onClick={() => navigate(`/invoices/${invoiceNum}`)}>
                Open collections view →
              </Button>
            }>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
                Each schedule line is its own customer document {invoiceNum}-Seq with its own PO.
                Per-installment upload / validate / send / override live in the collections view (one detail owns that flow).
              </div>
              <Table size="sm" style={{ fontSize: 12.5, marginBottom: 0 }}>
                <thead><tr><th>Seq</th><th>Sub-invoice</th><th>Your PO #</th><th style={{ textAlign: "right" }}>Amount</th><th>AP cap</th><th>Due</th><th>Match</th><th>Cleared</th></tr></thead>
                <tbody>
                  {installments.map((s) => {
                    const cap = capFromTerms(s.termsCode);
                    const head = cap > 0 ? cap - (s.amount || 0) : null;
                    return (
                      <tr key={s.id}>
                        <td>{s.seq}</td>
                        <td style={{ fontWeight: 600 }}>{s.customerInvoiceNumber}</td>
                        <td style={{ fontFamily: "monospace", fontSize: 12 }}>{s.customerPONumber || "—"}</td>
                        <td style={{ textAlign: "right" }}>{money(s.amount)}</td>
                        <td style={{ fontSize: 12, color: head != null && head < 0 ? "#DC2626" : "#16A34A" }}>
                          {cap > 0 ? (head < 0 ? `${money(-head)} OVER ${money(cap)}` : `${money(head)} clear of ${money(cap)}`) : "—"}
                        </td>
                        <td>{fmtDate(s.dueDate)}</td>
                        <td><Badge bg={MATCH_BADGE[s.matchStatus] || "secondary"}>{s.matchStatus}</Badge></td>
                        <td>
                          {s.sendStatus === "Sent" || s.poStatus === "Sent" ? <Badge bg="success">Sent</Badge>
                            : s.poStatus === "Validated" ? <Badge bg="info" title="Clean 3-way match — cleared to send">✓ Cleared</Badge>
                            : <Badge bg="secondary" title="Not cleared yet — held until this line's PO validates">Held</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Lines */}
          {lines.length > 0 && (
            <Card title={`📦 Lines (${lines.length})`}>
              <Table size="sm" style={{ fontSize: 12.5, marginBottom: 0 }}>
                <thead><tr><th>Line</th><th>Part / description</th><th style={{ textAlign: "right" }}>Qty</th><th style={{ textAlign: "right" }}>Unit</th><th style={{ textAlign: "right" }}>Ext</th></tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.invoiceLine ?? i + 1}</td>
                      <td>{l.partNum}<span style={{ color: "#94A3B8" }}>{l.lineDesc ? ` — ${l.lineDesc}` : ""}</span></td>
                      <td style={{ textAlign: "right" }}>{l.sellingShipQty ?? l.ourShipQty}</td>
                      <td style={{ textAlign: "right" }}>{money(l.docUnitPrice ?? l.unitPrice)}</td>
                      <td style={{ textAlign: "right" }}>{money(l.docExtPrice ?? l.extPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Linked order */}
          {order && (
            <Card title="🔗 Linked order">
              <Field label="Order">{order.orderNum} · {order.customerName || order.custID}</Field>
              <Field label="Order date">{fmtDate(order.orderDate)}</Field>
              <Field label="Order amount">{money(order.docOrderAmt ?? order.orderAmt)}</Field>
              <Field label="Order PO">{order.poNum}</Field>
            </Card>
          )}

          {/* Action bar */}
          {review && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "12px 0" }}>
              {!isCash && hasAction("00075", true) && (
                <Button variant="outline-secondary" disabled={!!busy} onClick={verifyInstallments}>
                  {busy === "verify-inst" ? <Spinner animation="border" size="sm" /> : "↻ Verify installments"}
                </Button>
              )}
              {hasAction("00076", true) && (
                <>
                  <Button variant="success" disabled={!!busy || review.reviewDecision === "Approved"} onClick={approve}>
                    {busy === "approve" ? <Spinner animation="border" size="sm" /> : review.reviewDecision === "Approved" ? "✓ Approved" : "✓ Approve"}
                  </Button>
                  <Button variant="outline-danger" disabled={!!busy} onClick={reject}>Reject</Button>
                  <Button variant="outline-warning" disabled={!!busy} onClick={holdToggle}>{review.isHeld ? "Unhold" : "🔒 Hold"}</Button>
                </>
              )}
              {hasAction("00077", true) && (
                <Button variant="primary"
                  disabled={!!busy || review.reviewDecision !== "Approved" || review.sendStatus === "Sent"}
                  title={review.sendStatus === "Sent" ? "Already sent." : review.reviewDecision !== "Approved" ? "Approve first." : undefined}
                  onClick={send}>
                  {busy === "send" ? <Spinner animation="border" size="sm" /> : "✉ Send"}
                </Button>
              )}
            </div>
          )}
          {!review && !loading && (
            <Alert variant="info" style={{ fontSize: 13 }}>
              No review row found for this invoice (it may predate the program or belong to an unposted batch) —
              showing the live Epicor data only.
            </Alert>
          )}
        </>
      )}
    </div>
  );
}
